import {
  useCallback,
  useEffect,
  useState,
  type MouseEventHandler,
} from "react";
import { getTooltipShouldStartAutoDismissTimer } from "./ctooltip-selectors";

interface UseTooltipAutoDismissInput {
  autoDismissMs?: number;
  isOpen: boolean;
  onDismiss?: () => void;
  pauseAutoDismissOnHover: boolean;
}

interface UseTooltipAutoDismissResult {
  onMouseEnter: MouseEventHandler<HTMLDivElement>;
  onMouseLeave: MouseEventHandler<HTMLDivElement>;
}

export function useTooltipAutoDismiss({
  autoDismissMs,
  isOpen,
  onDismiss,
  pauseAutoDismissOnHover,
}: UseTooltipAutoDismissInput): UseTooltipAutoDismissResult {
  const [isHovered, setIsHovered] = useState(false);
  const hasDismissHandler = Boolean(onDismiss);
  const shouldStartAutoDismissTimer = getTooltipShouldStartAutoDismissTimer({
    autoDismissMs,
    hasDismissHandler,
    isHovered,
    isOpen,
    pauseAutoDismissOnHover,
  });

  useEffect(() => {
    if (!shouldStartAutoDismissTimer || !onDismiss || autoDismissMs == null) {
      return;
    }

    const timerId = window.setTimeout(() => {
      onDismiss();
    }, autoDismissMs);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [autoDismissMs, onDismiss, shouldStartAutoDismissTimer]);

  const onMouseEnter = useCallback((): void => {
    setIsHovered(true);
  }, []);

  const onMouseLeave = useCallback((): void => {
    setIsHovered(false);
  }, []);

  return {
    onMouseEnter,
    onMouseLeave,
  };
}
